import { Ban, CheckCircle2, Lock, Package, PackageX } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { DeviceState } from '@/types/api';
import { cn } from '@/lib/cn';

/**
 * Where a device sits in its lifecycle. Like SeverityBadge, state is never carried
 * by colour alone — each one has its own icon shape and its own word, because the
 * difference between "revoked" and "claimed" decides whether a unit can dispatch an
 * ambulance and must survive a colour-blind reader and a greyscale printout.
 */
const styles: Record<DeviceState, { label: string; icon: LucideIcon; className: string; hint: string }> = {
  unclaimed: {
    label: 'Unclaimed',
    icon: Package,
    className: 'bg-surface-sunken text-ink-secondary border-line',
    hint: 'Provisioned and streaming, but nobody owns it yet.',
  },
  claimed: {
    label: 'Claimed',
    icon: CheckCircle2,
    className: 'bg-ok-soft text-ok border-ok/20',
    hint: 'Owned. Crashes on this device alert its owner.',
  },
  revoked: {
    label: 'Revoked',
    icon: Ban,
    className: 'bg-bad-soft text-bad border-bad/25',
    hint: 'Lost, stolen or compromised. Its telemetry is rejected.',
  },
  rma: {
    label: 'RMA',
    icon: PackageX,
    className: 'bg-surface-sunken text-ink-muted border-line',
    hint: 'Returned hardware. Terminal — this cannot be undone.',
  },
  locked: {
    label: 'Locked',
    icon: Lock,
    className: 'bg-warn-soft text-warn border-warn/20',
    hint: 'Too many failed claim attempts. Locked for 24 hours.',
  },
};

interface DeviceStateBadgeProps {
  state: DeviceState;
  size?: 'sm' | 'md';
}

export function DeviceStateBadge({ state, size = 'sm' }: DeviceStateBadgeProps) {
  const style = styles[state];
  const Icon = style.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-[12px]' : 'px-2.5 py-1 text-[13px]',
        style.className
      )}
      title={style.hint}
    >
      <Icon className={size === 'sm' ? 'size-3' : 'size-3.5'} aria-hidden />
      {style.label}
    </span>
  );
}
